import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { listingImageUrl } from '../utils/listingImageUrl';

interface ProductImageZoomProps {
  src: string;
  alt: string;
  className?: string;
  maxHeight?: number;
  /**
   * Already-loaded card/thumb display URL. Shown as the real main image first
   * (same bytes the grid cached), then upgraded to detail size in the background.
   */
  placeholderSrc?: string | null;
}

const LENS_SIZE = 100;
const ZOOM_SCALE = 4;
const FLYOUT_SIZE = LENS_SIZE * ZOOM_SCALE;
/** If the resize CDN hangs, fall back to the original URL. */
const LOAD_TIMEOUT_MS = 10000;

const ProductImageZoom: React.FC<ProductImageZoomProps> = ({
  src,
  alt,
  className = '',
  maxHeight = 600,
  placeholderSrc = null,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [zooming, setZooming] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const fullSrc = (src || '').trim();
  const detailSrc = listingImageUrl(fullSrc, 'detail') || fullSrc;
  const cardSrc = (placeholderSrc || '').trim() || listingImageUrl(fullSrc, 'card') || '';
  const startSrc = cardSrc || detailSrc;
  const [imgSrc, setImgSrc] = useState(startSrc);
  const [loading, setLoading] = useState(!cardSrc);

  const markLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  const imageIsReady = (img: HTMLImageElement | null) =>
    !!img && img.complete && img.naturalWidth > 0;

  // Paint the cached card image immediately, then upgrade to detail when ready.
  useEffect(() => {
    const nextCard = (placeholderSrc || '').trim() || listingImageUrl(fullSrc, 'card') || '';
    const nextDetail = listingImageUrl(fullSrc, 'detail') || fullSrc;
    const initial = nextCard || nextDetail;
    setImgSrc(initial);
    setLoading(!nextCard);
    setZooming(false);

    if (!nextDetail || nextDetail === initial) return;

    let cancelled = false;
    const pre = new Image();
    pre.decoding = 'async';
    pre.onload = () => {
      if (cancelled) return;
      setImgSrc(nextDetail);
      setLoading(false);
    };
    pre.onerror = () => {
      if (cancelled || !fullSrc || fullSrc === nextDetail) return;
      const fallback = new Image();
      fallback.onload = () => {
        if (!cancelled) {
          setImgSrc(fullSrc);
          setLoading(false);
        }
      };
      fallback.onerror = () => {
        if (!cancelled) setLoading(false);
      };
      fallback.src = fullSrc;
    };
    pre.src = nextDetail;
    return () => {
      cancelled = true;
    };
  }, [fullSrc, placeholderSrc]);

  useLayoutEffect(() => {
    if (imageIsReady(imgRef.current)) {
      markLoaded();
    }
  }, [imgSrc, markLoaded]);

  useEffect(() => {
    if (!loading) return;
    const t = window.setTimeout(() => {
      if (imgSrc !== fullSrc && fullSrc) {
        setImgSrc(fullSrc);
        setLoading(true);
      } else {
        markLoaded();
      }
    }, LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [loading, imgSrc, fullSrc, markLoaded]);

  const updatePosition = useCallback((clientX: number, clientY: number) => {
    const img = imgRef.current;
    if (!img || loading) return;

    const rect = img.getBoundingClientRect();
    if (
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      setZooming(false);
      return;
    }

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    setPos({ x, y });

    const gap = 16;
    let left = rect.right + gap;
    if (left + FLYOUT_SIZE > window.innerWidth - gap) {
      left = Math.max(gap, rect.left - FLYOUT_SIZE - gap);
    }

    let top = rect.top;
    if (top + FLYOUT_SIZE > window.innerHeight - gap) {
      top = Math.max(gap, window.innerHeight - FLYOUT_SIZE - gap);
    }

    setFlyoutPos({ top, left });
    setZooming(true);
  }, [loading]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      updatePosition(e.clientX, e.clientY);
    },
    [updatePosition]
  );

  const handleMouseLeave = useCallback(() => {
    setZooming(false);
  }, []);

  return (
  <>
    <div className={`relative block w-full max-w-full ${className}`}>
      <div
        className="relative flex w-full max-w-full items-center justify-center cursor-crosshair bg-gray-100 rounded-xl overflow-hidden"
        style={{ minHeight: loading ? Math.min(280, maxHeight) : undefined }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center"
            aria-busy="true"
            aria-label="Loading image"
          >
            <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-green-600 animate-spin" />
          </div>
        )}
        <img
          ref={imgRef}
          src={imgSrc}
          alt={alt}
          className={`w-full max-w-full h-auto object-contain block rounded-xl select-none transition-opacity duration-150 ${
            loading ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ maxHeight: `${maxHeight}px` }}
          draggable={false}
          decoding="async"
          onLoad={markLoaded}
          onError={() => {
            if (imgSrc !== fullSrc && fullSrc) {
              setImgSrc(fullSrc);
              setLoading(true);
            } else {
              markLoaded();
            }
          }}
        />

        {zooming && !loading && (
          <div
            className="absolute pointer-events-none hidden lg:block border border-gray-400 bg-white/25 shadow-sm"
            style={{
              width: LENS_SIZE,
              height: LENS_SIZE,
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              transform: 'translate(-50%, -50%)',
            }}
            aria-hidden
          />
        )}
      </div>
    </div>

    {zooming && !loading && (
      <div
        className="hidden lg:block fixed z-50 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl pointer-events-none"
        style={{
          top: flyoutPos.top,
          left: flyoutPos.left,
          width: FLYOUT_SIZE,
          height: FLYOUT_SIZE,
          backgroundImage: `url(${fullSrc})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${ZOOM_SCALE * 100}%`,
          backgroundPosition: `${pos.x}% ${pos.y}%`,
        }}
        aria-hidden
      />
    )}
  </>
  );
};

export default ProductImageZoom;
